import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Users, Check } from 'lucide-react';
import { PageHeader } from '@/cajita/ui';
import { CxDrawer } from '@/cajita/overlay';
import { useConfirm } from '@/shared/modals/ConfirmDialog';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { listParties, createParty, updateParty, deleteParty, type Party, type PartyInput } from './parties.api';
import { PartyForm } from './PartyForm';

const AV_COLORS = ['#84CC16', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4'];
const initials = (name: string): string =>
  (name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export function PartiesPage(): JSX.Element {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const search = useDebouncedValue(searchRaw, 300);
  const [drawer, setDrawer] = useState<{ mode: 'create' } | { mode: 'edit'; party: Party } | null>(null);

  const query = useQuery({
    queryKey: ['parties', { page, search }],
    queryFn: () => listParties({ page, pageSize: 20, search: search || undefined }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['parties'] });

  const createMut = useMutation({
    mutationFn: (input: PartyInput) => createParty(input),
    onSuccess: () => {
      toast.success('Cliente creado');
      setDrawer(null);
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; input: Partial<PartyInput> }) => updateParty(args.id, args.input),
    onSuccess: () => {
      toast.success('Cliente actualizado');
      setDrawer(null);
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteParty(id),
    onSuccess: () => {
      toast.success('Cliente eliminado');
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const handleDelete = async (p: Party) => {
    const ok = await confirm({ title: '¿Eliminar cliente?', description: `Se eliminará "${p.name}".`, variant: 'danger', confirmLabel: 'Eliminar' });
    if (ok) deleteMut.mutate(p.id);
  };

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Clientes"
          sub="Registra quiénes te compran y te venden · se validan RUC/DNI"
          actions={
            <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'create' })}>
              <Plus size={17} />
              Nuevo cliente
            </button>
          }
        />

        <div className="card">
          <div className="tbl-toolbar">
            <div className="input-icon" style={{ width: 320 }}>
              <Search size={17} />
              <input
                className="input"
                placeholder="Buscar por nombre, RUC o DNI…"
                value={searchRaw}
                onChange={(e) => {
                  setSearchRaw(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <span className="mla t-sm muted">{total} registros</span>
          </div>

          {query.isLoading ? (
            <SkeletonRows cols={5} />
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <Users size={28} />
              </div>
              <div>
                <div className="t-h2" style={{ marginBottom: 4 }}>
                  {search ? 'Sin resultados' : 'Sin clientes aún'}
                </div>
                <div className="muted">{search ? 'Prueba otra búsqueda.' : 'Crea tu primer cliente para empezar.'}</div>
              </div>
              {!search && (
                <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'create' })}>
                  <Plus size={17} />
                  Nuevo cliente
                </button>
              )}
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Nombre / Razón social</th>
                    <th>Contacto</th>
                    <th>Rol</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const c = AV_COLORS[(p.name?.length ?? 0) % AV_COLORS.length]!;
                    return (
                      <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ mode: 'edit', party: p })}>
                        <td>
                          <span className="badge badge-gray" style={{ marginRight: 6 }}>
                            {p.docType}
                          </span>
                          <span className="tnum strong">{p.docNumber}</span>
                        </td>
                        <td>
                          <div className="row aic gap10">
                            <span className="av" style={{ width: 32, height: 32, fontSize: 12, background: `${c}22`, color: c, borderColor: `${c}33` }}>
                              {initials(p.name)}
                            </span>
                            <span className="strong">{p.name}</span>
                          </div>
                        </td>
                        <td>{p.email || p.phone || <span className="muted">—</span>}</td>
                        <td>
                          <div className="row gap4">
                            {p.isCustomer && <span className="badge badge-lime">Cliente</span>}
                            {p.isSupplier && <span className="badge badge-blue">Proveedor</span>}
                          </div>
                        </td>
                        <td className="right" onClick={(e) => e.stopPropagation()}>
                          <div className="row gap4" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDrawer({ mode: 'edit', party: p })} title="Editar">
                              <Pencil size={16} />
                            </button>
                            <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} onClick={() => handleDelete(p)} title="Eliminar">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 20 && (
            <div className="tbl-foot">
              <span>
                Mostrando {items.length} de {total}
              </span>
              <div className="row gap6">
                <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <span className="t-sm muted" style={{ padding: '0 6px' }}>
                  {page} / {totalPages}
                </span>
                <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <CxDrawer
          title={drawer.mode === 'edit' ? drawer.party.name : 'Nuevo cliente'}
          sub={drawer.mode === 'edit' ? `${drawer.party.docType} ${drawer.party.docNumber}` : 'Registra un cliente o proveedor'}
          onClose={() => setDrawer(null)}
          footer={
            <>
              <button className="btn btn-secondary grow" onClick={() => setDrawer(null)}>
                Cancelar
              </button>
              <button className="btn btn-primary grow" type="submit" form="party-form" disabled={createMut.isPending || updateMut.isPending}>
                <Check size={18} />
                {createMut.isPending || updateMut.isPending ? 'Guardando…' : drawer.mode === 'edit' ? 'Guardar cambios' : 'Crear cliente'}
              </button>
            </>
          }
        >
          <PartyForm
            formId="party-form"
            defaultValues={drawer.mode === 'edit' ? drawer.party : null}
            onSubmit={(values) => {
              if (drawer.mode === 'edit') updateMut.mutate({ id: drawer.party.id, input: values });
              else createMut.mutate(values);
            }}
          />
        </CxDrawer>
      )}
    </div>
  );
}

function SkeletonRows({ cols, rows = 6 }: { cols: number; rows?: number }): JSX.Element {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="sk" style={{ height: 12, flex: c === 1 ? 2 : 1, opacity: 1 - r * 0.08 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function extractError(err: unknown): string {
  const resp = (err as { response?: { data?: { message?: string } } }).response?.data;
  return resp?.message ?? 'Ocurrió un error';
}
